import { useEffect, useRef, useState } from "react";
import * as Cesium from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import { MeasureTool, type MeasureMode, type MeasureResult } from "./measure";

type ProjectKind = "mesh" | "pointcloud";
type PointColorMode = "rgb" | "intensity" | "elevation";

type Props = {
  tilesetUrl: string;
  title?: string;
  kind?: ProjectKind;
};

const ionToken = import.meta.env.VITE_CESIUM_ION_TOKEN as string | undefined;
/** Lowest screen-space error the quality slider allowed. Lower means sharper tiles. */
const MAX_QUALITY_ERROR = 4;
/** Extra metres past the mesh edge so terrain skirts cannot draw over the border. */
const TERRAIN_CLIP_MARGIN_METERS = 2;
/** glTF is Y-up; 3D Tiles bounding boxes are Z-up. Same matrix Cesium applies on load. */
const GLTF_TO_3D_TILES = Cesium.Matrix4.fromRotationTranslation(
  Cesium.Matrix3.fromArray([1, 0, 0, 0, 0, 1, 0, -1, 0]),
);

type TilesetNode = {
  transform?: number[];
  boundingVolume?: { box?: number[] };
  content?: {
    uri?: string;
    transform?: number[];
    boundingVolume?: { box?: number[] };
  };
  children?: TilesetNode[];
};

/** Metashape root boxes are large cubes; the mesh is a thin layer inside them. */
function boxCorners(box: number[]): Cesium.Cartesian3[] {
  const [cx, cy, cz, xx, xy, xz, yx, yy, yz, zx, zy, zz] = box;
  const center = new Cesium.Cartesian3(cx, cy, cz);
  const x = new Cesium.Cartesian3(xx, xy, xz);
  const y = new Cesium.Cartesian3(yx, yy, yz);
  const z = new Cesium.Cartesian3(zx, zy, zz);
  const corners: Cesium.Cartesian3[] = [];
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const p = Cesium.Cartesian3.clone(center);
        Cesium.Cartesian3.add(
          p,
          Cesium.Cartesian3.multiplyByScalar(x, sx, new Cesium.Cartesian3()),
          p,
        );
        Cesium.Cartesian3.add(
          p,
          Cesium.Cartesian3.multiplyByScalar(y, sy, new Cesium.Cartesian3()),
          p,
        );
        Cesium.Cartesian3.add(
          p,
          Cesium.Cartesian3.multiplyByScalar(z, sz, new Cesium.Cartesian3()),
          p,
        );
        corners.push(p);
      }
    }
  }
  return corners;
}

function collectContentPoints(node: TilesetNode | undefined, transform: Cesium.Matrix4, out: Cesium.Cartesian3[]) {
  if (!node) return;
  const box = node.content?.boundingVolume?.box;
  if (box && box.length === 12) {
    for (const corner of boxCorners(box)) {
      out.push(Cesium.Matrix4.multiplyByPoint(transform, corner, new Cesium.Cartesian3()));
    }
  }
  for (const child of node.children ?? []) collectContentPoints(child, transform, out);
}

type FlatPoint = { x: number; y: number };

function convexHull(points: FlatPoint[]): FlatPoint[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (sorted.length <= 2) return sorted;
  const cross = (o: FlatPoint, a: FlatPoint, b: FlatPoint) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: FlatPoint[] = [];
  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper: FlatPoint[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const point = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function footprintFromPoints(points: Cesium.Cartesian3[], margin: number): Cesium.Cartesian3[] | undefined {
  if (points.length < 3) return undefined;
  const sphere = Cesium.BoundingSphere.fromPoints(points);
  const enu = Cesium.Transforms.eastNorthUpToFixedFrame(sphere.center);
  const inverse = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());
  const flat = points.map((point) => {
    const local = Cesium.Matrix4.multiplyByPoint(inverse, point, new Cesium.Cartesian3());
    return { x: local.x, y: local.y };
  });
  const hull = convexHull(flat);
  if (hull.length < 3) return undefined;
  let cx = 0;
  let cy = 0;
  for (const point of hull) {
    cx += point.x;
    cy += point.y;
  }
  cx /= hull.length;
  cy /= hull.length;
  return hull.map((point) => {
    const dx = point.x - cx;
    const dy = point.y - cy;
    const length = Math.hypot(dx, dy) || 1;
    const local = new Cesium.Cartesian3(point.x + (dx / length) * margin, point.y + (dy / length) * margin, 0);
    return Cesium.Matrix4.multiplyByPoint(enu, local, new Cesium.Cartesian3());
  });
}

function circleFootprint(sphere: Cesium.BoundingSphere, margin: number): Cesium.Cartesian3[] {
  const enu = Cesium.Transforms.eastNorthUpToFixedFrame(sphere.center);
  const radius = sphere.radius + margin;
  const positions: Cesium.Cartesian3[] = [];
  for (let i = 0; i < 24; i++) {
    const angle = (i / 24) * Math.PI * 2;
    const local = new Cesium.Cartesian3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
    positions.push(Cesium.Matrix4.multiplyByPoint(enu, local, new Cesium.Cartesian3()));
  }
  return positions;
}

type GltfNode = {
  mesh?: number;
  children?: number[];
  matrix?: number[];
  translation?: number[];
  rotation?: number[];
  scale?: number[];
};

type GltfJson = {
  meshes?: { primitives?: { attributes?: { POSITION?: number }; indices?: number }[] }[];
  nodes?: GltfNode[];
  scenes?: { nodes?: number[] }[];
  accessors?: {
    bufferView?: number;
    byteOffset?: number;
    componentType?: number;
    count?: number;
    type?: string;
  }[];
  bufferViews?: { byteOffset?: number; byteStride?: number }[];
};

function readIndices(view: DataView, json: GltfJson, binStart: number, binLength: number, index: number): number[] | undefined {
  const accessor = json.accessors?.[index];
  const bufferView = accessor?.bufferView != null ? json.bufferViews?.[accessor.bufferView] : undefined;
  if (!accessor || !bufferView || accessor.type !== "SCALAR" || !accessor.count) return undefined;
  const width = accessor.componentType === 5123 ? 2 : accessor.componentType === 5125 ? 4 : 0;
  if (!width) return undefined;
  const start = binStart + (bufferView.byteOffset ?? 0) + (accessor.byteOffset ?? 0);
  const values: number[] = [];
  for (let i = 0; i < accessor.count; i++) {
    const base = start + i * width;
    if (base + width > binStart + binLength) break;
    values.push(width === 2 ? view.getUint16(base, true) : view.getUint32(base, true));
  }
  return values;
}

/** Every open edge loop, largest first. A coarse mesh is often several disconnected buildings. */
function boundaryLoops(indices: number[]): number[][] {
  const count = new Map<string, number>();
  const bump = (a: number, b: number) => {
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    count.set(key, (count.get(key) ?? 0) + 1);
  };
  for (let i = 0; i + 2 < indices.length; i += 3) {
    bump(indices[i], indices[i + 1]);
    bump(indices[i + 1], indices[i + 2]);
    bump(indices[i + 2], indices[i]);
  }
  const adj = new Map<number, number[]>();
  const link = (a: number, b: number) => {
    const list = adj.get(a);
    if (list) list.push(b);
    else adj.set(a, [b]);
  };
  for (const [key, times] of count) {
    if (times !== 1) continue;
    const [a, b] = key.split(":").map(Number);
    link(a, b);
    link(b, a);
  }
  const loops: number[][] = [];
  const seen = new Set<string>();
  for (const [start, neighbors] of adj) {
    for (const next of neighbors) {
      if (seen.has(`${start}:${next}`)) continue;
      const loop = [start];
      let prev = start;
      let current = next;
      let guard = 0;
      while (current !== start && guard < indices.length) {
        seen.add(`${prev}:${current}`);
        seen.add(`${current}:${prev}`);
        loop.push(current);
        const onward = (adj.get(current) ?? []).find((candidate) => candidate !== prev);
        if (onward == null) break;
        prev = current;
        current = onward;
        guard += 1;
      }
      if (current === start && loop.length >= 3) loops.push(loop);
    }
  }
  loops.sort((a, b) => b.length - a.length);
  return loops.slice(0, 24);
}

function gltfNodeMatrix(node: GltfNode): Cesium.Matrix4 {
  if (node.matrix && node.matrix.length === 16) return Cesium.Matrix4.fromArray(node.matrix);
  const translation = node.translation ?? [0, 0, 0];
  const rotation = node.rotation ?? [0, 0, 0, 1];
  const scale = node.scale ?? [1, 1, 1];
  return Cesium.Matrix4.fromTranslationQuaternionRotationScale(
    new Cesium.Cartesian3(translation[0], translation[1], translation[2]),
    new Cesium.Quaternion(rotation[0], rotation[1], rotation[2], rotation[3]),
    new Cesium.Cartesian3(scale[0], scale[1], scale[2]),
  );
}

function meshWorldMatrices(json: GltfJson): Cesium.Matrix4[] {
  const count = json.meshes?.length ?? 0;
  const matrices = Array.from({ length: count }, () => Cesium.Matrix4.clone(Cesium.Matrix4.IDENTITY));
  const nodes = json.nodes ?? [];
  const visit = (index: number, parent: Cesium.Matrix4) => {
    const node = nodes[index];
    if (!node) return;
    const world = Cesium.Matrix4.multiply(parent, gltfNodeMatrix(node), new Cesium.Matrix4());
    if (node.mesh != null && matrices[node.mesh]) matrices[node.mesh] = world;
    for (const child of node.children ?? []) visit(child, world);
  };
  const roots = json.scenes?.[0]?.nodes ?? nodes.map((_, index) => index);
  for (const index of roots) visit(index, Cesium.Matrix4.IDENTITY);
  return matrices;
}

function readPositions(
  view: DataView,
  json: GltfJson,
  binStart: number,
  binLength: number,
  accessorIndex: number,
): Float32Array | undefined {
  const accessor = json.accessors?.[accessorIndex];
  const bufferView = accessor?.bufferView != null ? json.bufferViews?.[accessor.bufferView] : undefined;
  if (!accessor || !bufferView || accessor.componentType !== 5126 || accessor.type !== "VEC3" || !accessor.count) {
    return undefined;
  }
  const stride = bufferView.byteStride ?? 12;
  const start = binStart + (bufferView.byteOffset ?? 0) + (accessor.byteOffset ?? 0);
  const count = Math.min(accessor.count, 80000);
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const base = start + i * stride;
    if (base + 12 > binStart + binLength) break;
    positions[i * 3] = view.getFloat32(base, true);
    positions[i * 3 + 1] = view.getFloat32(base + 4, true);
    positions[i * 3 + 2] = view.getFloat32(base + 8, true);
  }
  return positions;
}

function transformPositions(positions: Float32Array, matrix: Cesium.Matrix4): Float32Array {
  if (Cesium.Matrix4.equals(matrix, Cesium.Matrix4.IDENTITY)) return positions;
  const out = new Float32Array(positions.length);
  const point = new Cesium.Cartesian3();
  for (let i = 0; i + 2 < positions.length; i += 3) {
    point.x = positions[i];
    point.y = positions[i + 1];
    point.z = positions[i + 2];
    Cesium.Matrix4.multiplyByPoint(matrix, point, point);
    out[i] = point.x;
    out[i + 1] = point.y;
    out[i + 2] = point.z;
  }
  return out;
}

function readGlbOutline(buffer: ArrayBuffer): { rings: Float32Array[]; loose?: Float32Array } | undefined {
  if (buffer.byteLength < 20) return undefined;
  const view = new DataView(buffer);
  if (view.getUint32(0, true) !== 0x46546c67) return undefined;
  let offset = 12;
  let json: GltfJson | undefined;
  let binStart = 0;
  let binLength = 0;
  while (offset + 8 <= buffer.byteLength) {
    const chunkLength = view.getUint32(offset, true);
    const chunkType = view.getUint32(offset + 4, true);
    const chunkStart = offset + 8;
    if (chunkType === 0x4e4f534a) {
      json = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, chunkStart, chunkLength))) as GltfJson;
    } else if (chunkType === 0x004e4942) {
      binStart = chunkStart;
      binLength = chunkLength;
    }
    offset = chunkStart + chunkLength;
  }
  if (!json || binLength === 0 || !json.meshes?.length) return undefined;
  const matrices = meshWorldMatrices(json);
  const rings: Float32Array[] = [];
  const loose: number[] = [];
  for (let meshIndex = 0; meshIndex < json.meshes.length; meshIndex++) {
    const matrix = matrices[meshIndex] ?? Cesium.Matrix4.IDENTITY;
    for (const primitive of json.meshes[meshIndex].primitives ?? []) {
      if (primitive.attributes?.POSITION == null) continue;
      const positions = readPositions(view, json, binStart, binLength, primitive.attributes.POSITION);
      if (!positions || positions.length < 9) continue;
      const placed = transformPositions(positions, matrix);
      const indices = primitive.indices != null ? readIndices(view, json, binStart, binLength, primitive.indices) : undefined;
      const loops = indices ? boundaryLoops(indices) : [];
      if (loops.length === 0) {
        for (let i = 0; i < placed.length; i++) loose.push(placed[i]);
        continue;
      }
      for (const loop of loops) {
        const out = new Float32Array(loop.length * 3);
        let valid = true;
        for (let i = 0; i < loop.length; i++) {
          const vertex = loop[i] * 3;
          if (vertex + 2 >= placed.length) {
            valid = false;
            break;
          }
          out[i * 3] = placed[vertex];
          out[i * 3 + 1] = placed[vertex + 1];
          out[i * 3 + 2] = placed[vertex + 2];
        }
        if (valid) rings.push(out);
      }
    }
  }
  if (rings.length === 0 && loose.length < 9) return undefined;
  return { rings, loose: loose.length >= 9 ? Float32Array.from(loose) : undefined };
}

function resolveAgainst(baseUrl: string, relative: string): string {
  const base = baseUrl.startsWith("http") ? baseUrl : new URL(baseUrl, window.location.href).toString();
  return new URL(relative, base).toString();
}

function offsetOutline(points: Cesium.Cartesian3[], margin: number): Cesium.Cartesian3[] | undefined {
  if (points.length < 3) return undefined;
  const sphere = Cesium.BoundingSphere.fromPoints(points);
  const enu = Cesium.Transforms.eastNorthUpToFixedFrame(sphere.center);
  const inverse = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());
  const flat = points.map((point) => {
    const local = Cesium.Matrix4.multiplyByPoint(inverse, point, new Cesium.Cartesian3());
    return { x: local.x, y: local.y };
  });
  let area = 0;
  for (let i = 0; i < flat.length; i++) {
    const a = flat[i];
    const b = flat[(i + 1) % flat.length];
    area += a.x * b.y - b.x * a.y;
  }
  if (area < 0) flat.reverse();
  const offset = flat.map((point, index) => {
    const prev = flat[(index + flat.length - 1) % flat.length];
    const next = flat[(index + 1) % flat.length];
    const e1x = point.x - prev.x;
    const e1y = point.y - prev.y;
    const e2x = next.x - point.x;
    const e2y = next.y - point.y;
    const e1 = Math.hypot(e1x, e1y) || 1;
    const e2 = Math.hypot(e2x, e2y) || 1;
    const ox = e1y / e1 + e2y / e2;
    const oy = -e1x / e1 - e2x / e2;
    const length = Math.hypot(ox, oy) || 1;
    return { x: point.x + (ox / length) * margin, y: point.y + (oy / length) * margin };
  });
  return offset.map((point) =>
    Cesium.Matrix4.multiplyByPoint(enu, new Cesium.Cartesian3(point.x, point.y, 0), new Cesium.Cartesian3()),
  );
}

function toEcefRing(model: Cesium.Matrix4, positions: Float32Array): Cesium.Cartesian3[] {
  const points: Cesium.Cartesian3[] = [];
  for (let i = 0; i + 2 < positions.length; i += 3) {
    points.push(
      Cesium.Matrix4.multiplyByPoint(
        model,
        new Cesium.Cartesian3(positions[i], positions[i + 1], positions[i + 2]),
        new Cesium.Cartesian3(),
      ),
    );
  }
  return points;
}

async function coarseMeshPoints(
  tilesetUrl: string,
  root: TilesetNode,
  tileTransform: Cesium.Matrix4,
): Promise<{ rings: Cesium.Cartesian3[][]; loose: Cesium.Cartesian3[] } | undefined> {
  const uri = root.content?.uri;
  if (!uri || !/\.glb(?:$|\?)/i.test(uri)) return undefined;
  const response = await fetch(resolveAgainst(tilesetUrl, uri));
  if (!response.ok) return undefined;
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength > 8_000_000) return undefined;
  const outline = readGlbOutline(buffer);
  if (!outline) return undefined;
  const contentTransform = root.content?.transform
    ? Cesium.Matrix4.fromArray(root.content.transform)
    : Cesium.Matrix4.IDENTITY;
  const oriented = Cesium.Matrix4.multiply(contentTransform, GLTF_TO_3D_TILES, new Cesium.Matrix4());
  const model = Cesium.Matrix4.multiply(tileTransform, oriented, new Cesium.Matrix4());
  return {
    rings: outline.rings.map((ring) => toEcefRing(model, ring)),
    loose: outline.loose ? toEcefRing(model, outline.loose) : [],
  };
}

/** Root plus child overview meshes. One coarse file often leaves later buildings uncovered. */
async function collectMeshOutlines(
  tilesetUrl: string,
  node: TilesetNode | undefined,
  transform: Cesium.Matrix4,
  rings: Cesium.Cartesian3[][],
  loose: Cesium.Cartesian3[],
  budget: { left: number },
) {
  if (!node || budget.left <= 0) return;
  if (node.content?.uri && /\.glb(?:$|\?)/i.test(node.content.uri)) {
    budget.left -= 1;
    const mesh = await coarseMeshPoints(tilesetUrl, node, transform).catch(() => undefined);
    if (mesh) {
      rings.push(...mesh.rings);
      loose.push(...mesh.loose);
    }
  }
  for (const child of node.children ?? []) {
    const childTransform = child.transform
      ? Cesium.Matrix4.multiply(transform, Cesium.Matrix4.fromArray(child.transform), new Cesium.Matrix4())
      : transform;
    await collectMeshOutlines(tilesetUrl, child, childTransform, rings, loose, budget);
  }
}

type ContentBounds = {
  sphere: Cesium.BoundingSphere;
  footprints: Cesium.Cartesian3[][];
};

async function contentBounds(tilesetUrl: string): Promise<ContentBounds | undefined> {
  const response = await fetch(tilesetUrl);
  if (!response.ok) return undefined;
  const json = (await response.json()) as { root?: TilesetNode };
  const transform = json.root?.transform
    ? Cesium.Matrix4.fromArray(json.root.transform)
    : Cesium.Matrix4.IDENTITY;
  const points: Cesium.Cartesian3[] = [];
  collectContentPoints(json.root, transform, points);
  if (points.length < 2) return undefined;
  const sphere = Cesium.BoundingSphere.fromPoints(points);
  const rings: Cesium.Cartesian3[][] = [];
  const loose: Cesium.Cartesian3[] = [];
  await collectMeshOutlines(tilesetUrl, json.root, transform, rings, loose, { left: 16 });
  const footprints: Cesium.Cartesian3[][] = [];
  for (const ring of rings) {
    if (Cesium.BoundingSphere.fromPoints(ring).radius < 4) continue;
    const outline = offsetOutline(ring, TERRAIN_CLIP_MARGIN_METERS) ?? footprintFromPoints(ring, TERRAIN_CLIP_MARGIN_METERS);
    if (outline) footprints.push(outline);
  }
  const looseHull = loose.length >= 3 ? footprintFromPoints(loose, TERRAIN_CLIP_MARGIN_METERS) : undefined;
  if (looseHull) footprints.push(looseHull);
  if (footprints.length > 40) {
    footprints.sort((a, b) => b.length - a.length);
    const extra = footprintFromPoints(footprints.splice(40).flat(), TERRAIN_CLIP_MARGIN_METERS);
    if (extra) footprints.push(extra);
  }
  if (footprints.length === 0) {
    const fallback = footprintFromPoints(points, TERRAIN_CLIP_MARGIN_METERS) ?? circleFootprint(sphere, TERRAIN_CLIP_MARGIN_METERS);
    footprints.push(fallback);
  }
  return { sphere, footprints };
}

function clipGlobeToFootprint(viewer: Cesium.Viewer, footprints: Cesium.Cartesian3[][] | null) {
  if (!Cesium.ClippingPolygonCollection.isSupported(viewer.scene)) return;
  const globe = viewer.scene.globe;
  const polygons = (footprints ?? []).filter((positions) => positions.length >= 3);
  if (polygons.length === 0) {
    if (globe.clippingPolygons) globe.clippingPolygons.enabled = false;
    return;
  }
  globe.clippingPolygons = new Cesium.ClippingPolygonCollection({
    polygons: polygons.map((positions) => new Cesium.ClippingPolygon({ positions })),
    inverse: false,
    enabled: true,
  });
}

async function enableSatelliteMap(viewer: Cesium.Viewer) {
  viewer.imageryLayers.removeAll();
  viewer.imageryLayers.addImageryProvider(await Cesium.IonImageryProvider.fromAssetId(2));
  // Imagery alone sits on the ellipsoid. The mesh is hundreds of metres up until terrain is on.
  viewer.scene.setTerrain(Cesium.Terrain.fromWorldTerrain());
}

function disableSatelliteMap(viewer: Cesium.Viewer) {
  viewer.imageryLayers.removeAll();
  viewer.scene.setTerrain(
    new Cesium.Terrain(Promise.resolve(new Cesium.EllipsoidTerrainProvider())),
  );
}

function frameOffset(sphere: Cesium.BoundingSphere) {
  return new Cesium.HeadingPitchRange(
    Cesium.Math.toRadians(25),
    Cesium.Math.toRadians(-42),
    Math.max(sphere.radius * 2.15, 40),
  );
}

/** Grab-pan for the nadir orthographic camera. Translate is ignored in 3D scene mode. */
function panOrthographic(viewer: Cesium.Viewer, dx: number, dy: number) {
  const frustum = viewer.camera.frustum;
  if (!(frustum instanceof Cesium.OrthographicFrustum) || !frustum.width) return;
  const metersPerPixel = frustum.width / (viewer.scene.canvas.clientWidth || 1);
  viewer.camera.moveRight(-dx * metersPerPixel);
  viewer.camera.moveUp(dy * metersPerPixel);
}

function applyPointCloudStyle(tileset: Cesium.Cesium3DTileset, colorMode: PointColorMode) {
  const shading = tileset.pointCloudShading;
  shading.attenuation = true;
  shading.eyeDomeLighting = true;
  shading.eyeDomeLightingStrength = 1.2;
  shading.maximumAttenuation = 10;

  if (colorMode === "rgb") {
    tileset.style = new Cesium.Cesium3DTileStyle({
      pointSize: "3.0",
    });
    return;
  }
  if (colorMode === "intensity") {
    tileset.style = new Cesium.Cesium3DTileStyle({
      color: "color('white') * (${INTENSITY} / 65535.0)",
      pointSize: "3.0",
    });
    return;
  }
  // Elevation: hue by absolute height (metres above ellipsoid).
  tileset.style = new Cesium.Cesium3DTileStyle({
    color: "hsl(clamp((${POSITION_ABSOLUTE}.z - 250.0) / 150.0, 0.0, 1.0) * 0.7, 1.0, 0.55)",
    pointSize: "3.0",
  });
}

export function CesiumViewer({ tilesetUrl, title, kind = "mesh" }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const tilesetRef = useRef<Cesium.Cesium3DTileset | null>(null);
  const frameRef = useRef<Cesium.BoundingSphere | null>(null);
  const footprintRef = useRef<Cesium.Cartesian3[][] | null>(null);
  const mapOnRef = useRef(false);
  const measureRef = useRef<MeasureTool | null>(null);
  const [mode, setMode] = useState<MeasureMode>("none");
  const [result, setResult] = useState<MeasureResult | null>(null);
  const [mapOn, setMapOn] = useState(false);
  const [topView, setTopView] = useState(false);
  const [pointColor, setPointColor] = useState<PointColorMode>("rgb");
  const isPointCloud = kind === "pointcloud";
  const topViewRef = useRef(false);
  const controllerDefaults = useRef<{
    rotate: Cesium.CameraEventType | Cesium.CameraEventType[];
    tilt: Cesium.CameraEventType | Cesium.CameraEventType[];
    translate: Cesium.CameraEventType | Cesium.CameraEventType[];
  } | null>(null);
  const [status, setStatus] = useState("Loading model…");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    const update = () => {
      const viewport = window.visualViewport;
      const height = viewport?.height ?? window.innerHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      root.style.setProperty("--vv-height", `${Math.round(height)}px`);
      root.style.setProperty("--vv-offset-top", `${Math.round(offsetTop)}px`);
    };
    update();
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      root.style.removeProperty("--vv-height");
      root.style.removeProperty("--vv-offset-top");
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    if (ionToken) {
      Cesium.Ion.defaultAccessToken = ionToken;
    }

    const viewer = new Cesium.Viewer(containerRef.current, {
      animation: false,
      timeline: false,
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      baseLayer: false,
      terrain: undefined,
      skyBox: false,
      skyAtmosphere: false,
      orderIndependentTranslucency: false,
      contextOptions: {
        webgl: {
          alpha: false,
          antialias: true,
          preserveDrawingBuffer: true,
        },
      },
    });

    viewer.scene.globe.show = true;
    viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString("#1a2332");
    viewer.scene.globe.enableLighting = false;
    viewer.scene.globe.showGroundAtmosphere = false;
    viewer.scene.sun = undefined;
    viewer.scene.moon = undefined;
    viewer.scene.fog.enabled = false;
    viewer.scene.postProcessStages.fxaa.enabled = true;
    viewer.scene.globe.depthTestAgainstTerrain = true;
    viewer.scene.pickTranslucentDepth = true;
    viewer.scene.highDynamicRange = false;
    viewer.shadows = false;
    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString("#070b14");

    // Soft photogrammetry-friendly lighting (textures are already baked)
    viewer.scene.light = new Cesium.DirectionalLight({
      direction: new Cesium.Cartesian3(0.2, 0.5, -0.8),
      intensity: 2.0,
    });

    viewer.imageryLayers.removeAll();
    if (ionToken) {
      void enableSatelliteMap(viewer)
        .then(() => {
          if (viewerRef.current !== viewer) return;
          mapOnRef.current = true;
          setMapOn(true);
          clipGlobeToFootprint(viewer, footprintRef.current);
        })
        .catch((err) => {
          console.error(err);
        });
    }

    viewerRef.current = viewer;
    // Default double-click flies to the picked entity and leaves the top-down view.
    viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    measureRef.current = new MeasureTool(viewer, setResult);

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        measureRef.current?.cancel();
        setMode("none");
        measureRef.current?.setMode("none");
      }
    };
    window.addEventListener("keydown", onKey);

    const pan = { active: false, x: 0, y: 0, moved: false };
    const onPanDown = (ev: PointerEvent) => {
      if (!topViewRef.current || ev.button !== 0) return;
      pan.active = true;
      pan.moved = false;
      pan.x = ev.clientX;
      pan.y = ev.clientY;
    };
    const onPanMove = (ev: PointerEvent) => {
      if (!pan.active || !topViewRef.current) return;
      const dx = ev.clientX - pan.x;
      const dy = ev.clientY - pan.y;
      if (!pan.moved && dx * dx + dy * dy < 9) return;
      pan.moved = true;
      pan.x = ev.clientX;
      pan.y = ev.clientY;
      panOrthographic(viewer, dx, dy);
    };
    const onPanUp = () => {
      pan.active = false;
    };
    viewer.scene.canvas.addEventListener("pointerdown", onPanDown);
    viewer.scene.canvas.addEventListener("pointermove", onPanMove);
    window.addEventListener("pointerup", onPanUp);

    return () => {
      window.removeEventListener("keydown", onKey);
      viewer.scene.canvas.removeEventListener("pointerdown", onPanDown);
      viewer.scene.canvas.removeEventListener("pointermove", onPanMove);
      window.removeEventListener("pointerup", onPanUp);
      measureRef.current?.destroy();
      measureRef.current = null;
      viewer.destroy();
      viewerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !tilesetUrl) return;
    let cancelled = false;

    (async () => {
      try {
        setStatus("Loading tileset…");
        setError(null);
        if (tilesetRef.current) {
          viewer.scene.primitives.remove(tilesetRef.current);
          tilesetRef.current = null;
        }
        const tileset = await Cesium.Cesium3DTileset.fromUrl(tilesetUrl, {
          maximumScreenSpaceError: MAX_QUALITY_ERROR,
          enableCollision: true,
        });
        if (cancelled) {
          tileset.destroy();
          return;
        }
        // Prefer baked textures over harsh scene lighting
        tileset.imageBasedLighting.imageBasedLightingFactor = new Cesium.Cartesian2(1.0, 1.0);
        if (isPointCloud) {
          applyPointCloudStyle(tileset, "rgb");
        }
        viewer.scene.primitives.add(tileset);
        tilesetRef.current = tileset;
        const bounds = isPointCloud
          ? undefined
          : await contentBounds(tilesetUrl).catch(() => undefined);
        if (cancelled) return;
        const sphere = bounds?.sphere ?? tileset.boundingSphere;
        frameRef.current = sphere;
        footprintRef.current = bounds?.footprints ?? [
          circleFootprint(sphere, TERRAIN_CLIP_MARGIN_METERS + (isPointCloud ? 4 : 0)),
        ];
        clipGlobeToFootprint(viewer, mapOnRef.current ? footprintRef.current : null);
        await viewer.camera.flyToBoundingSphere(sphere, {
          duration: 0,
          offset: frameOffset(sphere),
        });
        setStatus("Ready");
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err.message : "Failed to load tileset");
        setStatus("Error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tilesetUrl, isPointCloud]);

  useEffect(() => {
    const tileset = tilesetRef.current;
    if (!tileset || !isPointCloud) return;
    try {
      applyPointCloudStyle(tileset, pointColor);
    } catch (err) {
      console.warn("Point color style failed", err);
      if (pointColor !== "elevation") {
        applyPointCloudStyle(tileset, "elevation");
        setPointColor("elevation");
      }
    }
  }, [pointColor, isPointCloud]);

  useEffect(() => {
    measureRef.current?.setMode(mode);
    if (mode === "none") setResult(null);
    else if (mode === "area") {
      setResult({
        label: "Area",
        detail: "Click corners on the model. Double-click to close.",
      });
    } else {
      setResult({
        label: "Distance",
        detail: "Click points on the model. The yellow line is the height difference.",
      });
    }
  }, [mode]);

  const leaveTopView = (viewer: Cesium.Viewer) => {
    const controller = viewer.scene.screenSpaceCameraController;
    const defaults = controllerDefaults.current;
    topViewRef.current = false;
    viewer.camera.switchToPerspectiveFrustum();
    controller.enableRotate = true;
    controller.enableTilt = true;
    controller.enableLook = true;
    if (defaults) {
      controller.rotateEventTypes = defaults.rotate;
      controller.tiltEventTypes = defaults.tilt;
      controller.translateEventTypes = defaults.translate;
    }
    setTopView(false);
  };

  const fitModel = async () => {
    const viewer = viewerRef.current;
    const sphere = frameRef.current ?? tilesetRef.current?.boundingSphere;
    if (!viewer || !sphere) return;
    leaveTopView(viewer);
    await viewer.camera.flyToBoundingSphere(sphere, {
      duration: 1.1,
      offset: frameOffset(sphere),
    });
  };

  const viewTop = () => {
    const viewer = viewerRef.current;
    const sphere = frameRef.current ?? tilesetRef.current?.boundingSphere;
    if (!viewer || !sphere) return;
    if (topView) {
      void fitModel();
      return;
    }
    const controller = viewer.scene.screenSpaceCameraController;
    if (!controllerDefaults.current) {
      controllerDefaults.current = {
        rotate: controller.rotateEventTypes ?? Cesium.CameraEventType.LEFT_DRAG,
        tilt: controller.tiltEventTypes ?? Cesium.CameraEventType.MIDDLE_DRAG,
        translate: controller.translateEventTypes ?? Cesium.CameraEventType.RIGHT_DRAG,
      };
    }
    const center = sphere.center;
    const carto = Cesium.Cartographic.fromCartesian(center);
    const range = Math.max(sphere.radius * 2.6, 40);
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromRadians(
        carto.longitude,
        carto.latitude,
        carto.height + range,
      ),
      orientation: {
        heading: 0,
        pitch: Cesium.Math.toRadians(-90),
        roll: 0,
      },
      duration: 0.8,
      complete: () => {
        viewer.camera.switchToOrthographicFrustum();
        controller.enableTilt = false;
        controller.enableRotate = false;
        controller.enableLook = false;
        topViewRef.current = true;
        setTopView(true);
      },
    });
  };

  const northUp = () => {
    const viewer = viewerRef.current;
    const sphere = frameRef.current ?? tilesetRef.current?.boundingSphere;
    if (!viewer || !sphere) return;
    leaveTopView(viewer);
    const center = sphere.center;
    const carto = Cesium.Cartographic.fromCartesian(center);
    const range = Math.max(sphere.radius * 2.4, 40);
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromRadians(
        carto.longitude,
        carto.latitude,
        carto.height + range,
      ),
      orientation: {
        heading: 0,
        pitch: Cesium.Math.toRadians(-90),
        roll: 0,
      },
      duration: 1,
    });
  };

  const toggleMap = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    if (!mapOn) {
      if (!ionToken) {
        setResult({
          label: "Map unavailable",
          detail: "Set VITE_CESIUM_ION_TOKEN to enable satellite imagery",
        });
        return;
      }
      await enableSatelliteMap(viewer);
      mapOnRef.current = true;
      setMapOn(true);
      clipGlobeToFootprint(viewer, footprintRef.current);
    } else {
      disableSatelliteMap(viewer);
      mapOnRef.current = false;
      setMapOn(false);
      clipGlobeToFootprint(viewer, null);
    }
  };

  return (
    <div className="viewer-shell">
      <div ref={containerRef} className="cesium-host" />

      <div className="viewer-top">
        <div className="viewer-brand">
          <img className="brand-logo" src="/dronevision-logo.png" alt="DroneVision" />
          <div>
            <div className="viewer-title">{title ?? "3D Model"}</div>
            <div className="viewer-status">{status}</div>
          </div>
        </div>
      </div>

      <div className="viewer-bottom">
      <div className="viewer-help">
        {mode === "area"
          ? "Click corners · Double-click to close · Drag corners to adjust · Esc cancels"
          : mode === "distance"
            ? "Click points · yellow line is the height difference · Esc cancels"
            : topView
              ? "Left drag pan · Scroll zoom · 2D again or Fit for 3D"
              : "Left drag orbit · Right drag pan · Scroll zoom"}
      </div>
      <div className="viewer-toolbar">
        <button type="button" className="tool-btn" onClick={fitModel} title="Fit model">
          Fit
        </button>
        <button type="button" className="tool-btn" onClick={northUp} title="Face north">
          North
        </button>
        <button
          type="button"
          className={`tool-btn ${topView ? "active" : ""}`}
          onClick={viewTop}
          title="Top-down 2D view"
        >
          2D
        </button>
        <button
          type="button"
          className={`tool-btn ${mapOn ? "active" : ""}`}
          onClick={toggleMap}
          title="Toggle satellite map"
        >
          Map
        </button>
        {isPointCloud && (
          <>
            <div className="tool-divider" />
            <button
              type="button"
              className={`tool-btn ${pointColor === "rgb" ? "active" : ""}`}
              onClick={() => setPointColor("rgb")}
              title="Color from scan RGB"
            >
              RGB
            </button>
            <button
              type="button"
              className={`tool-btn ${pointColor === "intensity" ? "active" : ""}`}
              onClick={() => setPointColor("intensity")}
              title="Color from laser intensity"
            >
              Intensity
            </button>
            <button
              type="button"
              className={`tool-btn ${pointColor === "elevation" ? "active" : ""}`}
              onClick={() => setPointColor("elevation")}
              title="Color by elevation"
            >
              Elevation
            </button>
          </>
        )}
        <div className="tool-divider" />
        <button
          type="button"
          className={`tool-btn ${mode === "distance" ? "active" : ""}`}
          onClick={() => setMode(mode === "distance" ? "none" : "distance")}
          title="Measure distance"
        >
          Distance
        </button>
        <button
          type="button"
          className={`tool-btn ${mode === "area" ? "active" : ""}`}
          onClick={() => setMode(mode === "area" ? "none" : "area")}
          title="Measure area"
        >
          Area
        </button>
        <button
          type="button"
          className="tool-btn"
          onClick={() => {
            measureRef.current?.clearAll();
            setMode("none");
          }}
          title="Clear measurements"
        >
          Clear
        </button>
      </div>
      </div>

      {result && (
        <div className="measure-panel">
          <div className="measure-label">{result.label}</div>
          {result.detail && <div className="measure-detail">{result.detail}</div>}
        </div>
      )}

      {error && <div className="viewer-error">{error}</div>}
    </div>
  );
}
